import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user, token, API } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchCart = useCallback(async () => {
    if (!user || !token) { setCart({ items: [], total: 0 }); setCartCount(0); return; }
    try {
      setLoading(true);
      const res = await axios.get(`${API}/cart`, { headers: { Authorization: `Bearer ${token}` } });
      setCart(res.data);
      setCartCount(res.data.items?.reduce((a, i) => a + i.quantity, 0) || 0);
    } catch (err) {
      console.error('Error fetching cart', err);
    } finally {
      setLoading(false);
    }
  }, [user, token, API]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = async (productId, quantity = 1) => {
    await axios.post(`${API}/cart/add`, { product_id: productId, quantity }, { headers });
    await fetchCart();
  };

  const updateQuantity = async (productId, quantity) => {
    await axios.put(`${API}/cart/update`, { product_id: productId, quantity }, { headers });
    await fetchCart();
  };

  const removeItem = async (productId) => {
    await axios.delete(`${API}/cart/remove/${productId}`, { headers });
    await fetchCart();
  };

  const clearCart = async () => {
    await axios.delete(`${API}/cart/clear`, { headers });
    await fetchCart();
  };

  return (
    <CartContext.Provider value={{ cart, cartCount, loading, fetchCart, addToCart, updateQuantity, removeItem, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
